import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Spinner,
  Tabs,
  Text,
} from "@radix-ui/themes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import CheckboxGroupComponent from "../../components/inputs/CheckboxGroup/CheckboxGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetPaymentTypesQuery } from "../../hooks/useGetPaymentTypesQuery";
import { useGetProcessModelsQuery } from "../../hooks/useGetProcessModelsQuery";
import { useGetSchoolBankConfigQuery } from "../../hooks/useGetSchoolBankConfigQuery";
import { useGetTeacheIdByUserId } from "../../hooks/useGetTeacheIdByUserId";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import {
  resetStudentData,
  setStudentData,
} from "../../redux/slices/school/student";
import { setSubscriptionData } from "../../redux/slices/school/subscription";
import { resetUserData, setUserData } from "../../redux/slices/users/user";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import {
  createPaymentReference,
  getReferenceCodeV2,
} from "../../services/finance/paymentReference";
import { createPaymentTransaction } from "../../services/finance/paymentTransaction";
import {
  createStudentDebt,
  createStudentDebtReferences,
} from "../../services/finance/studentDebt";
import { getPlanSubjects } from "../../services/school/PlanSubjects";
import {
  createSubscription,
  createSubscriptionSubject,
} from "../../services/school/student/createSubscription";
import { getStudentById } from "../../services/school/student/getStudentByCode";
import { getSubjectsReprovedQuery } from "../../services/school/subject/getSubjects";
import { getUserById } from "../../services/user/getUsers";
import {
  CreateStudentDebtReferenceType,
  PaymentReferenceType,
  PaymentTransactionType,
} from "../../types/Finance";
import { SubscriptionType } from "../../types/Student";
import { processModelName_preSubscription } from "../../utils/constants";
import { addDaysToDate, formatDateForInput } from "../../utils/dateFormatter";
import { getFile } from "../../utils/fileUtils";
import { stringToBoolean } from "../../utils/utilities";
import { UserPreview } from "../Users/UserPreview";
import { CreateSubscriptionFormValidation } from "./CreateSubscriptionFormValidation";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import {
  useGetStudentDebtiesQuery,
  useGetStudentDebtsTotalByStudentIdQuery,
} from "../../hooks/useGetStudentDebtiesQuery";
import { getCoursePayments } from "../../services/finance/coursePayments";
import { setReferencesData } from "../../redux/slices/finance/reference";
import { isEmpty } from "lodash";
import { getBankNameByEntity } from "../Student/getBankNameByEntity";
import {
  setLoaderAvailable,
  resetLoaderAvailable,
} from "../../redux/slices/loaderController";
import { IES } from "../../utils/labels";
import { useExportDocument } from "../../hooks/useExportDocument";
import { useIES } from "../../hooks/useIES";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
type ActiveSegmentTab =
  | "user"
  | "documents"
  | "departament"
  | "subjects"
  | "subscription"
  | "payments"
  | "validate";

const columnNamesMapPlanSubjects: { [key: string]: string } = {
  level: "Ano",
  semester: "Semestre",
  subject: "Disciplina",
  isCoreBagde: "Nuclear/Opcional",
};

type PaymentPrintData = {
  isPaymentByEntityReference?: boolean;
  reference?: string;
  entity?: string;
  bank?: string;
  totalPrice?: number;
};

export const CreateMySubscription = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: currentYear } = useGetCurrentAcademicYearQuery();
  const { exportDocumentPdf: exportDocument, downloadOnly } =
    useExportDocument();
  const loginData = useSelector((state: RootState) => state.login.userData);
  const { logoBase64 } = useLogoBase64();
  const { IES } = useIES();
  const navigate = useNavigate();

  const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>("user");
  const studentData = useSelector((state: RootState) => state.student.student);
  const userData = useSelector((state: RootState) => state.user);

  const { UserId } = useSelector((state: RootState) => state.login.userData);
  const { data: student, isLoading: isLoadingStudent } =
    useGetTeacheIdByUserId(UserId);
  const mutationCreateSubscription = useMutation({
    mutationFn: createSubscription,
    mutationKey: "createSubscription",
  });
  const mutationCreateSubscriptionSubject = useMutation({
    mutationFn: createSubscriptionSubject,
    mutationKey: "createSubscriptionSubject",
  });
  const mutationSaveReferenceCode = useMutation({
    mutationFn: createPaymentReference,
    mutationKey: "createPaymentReference",
  });
  const mutationCreatePaymentTransaction = useMutation({
    mutationFn: createPaymentTransaction,
    mutationKey: "createPaymentTransaction",
  });

  const {
    data: schoolBankConfigsData,
    status: schoolBankConfigsStatus,
    isError: isErrorSchoolschoolBankConfigs,
    isLoading: isLoadingSchoolschoolBankConfigs,
  } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
  const {
    data: paymentTypesData,
    status: paymentTypesStatus,
    isError: isErrorPaymentTypes,
    isLoading: isLoadingPaymentTypes,
  } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });

  const mutationCreateStudentDebt = useMutation({
    mutationFn: createStudentDebt,
    mutationKey: "createStudentDebt",
  });

  const dispatch = useAppDispatch();

  const defaultValues = {};

  const { handleSubmit } = useForm({
    defaultValues,
    mode: "onBlur",
  });
  const { data: studentDebtsTotalsData } =
    useGetStudentDebtsTotalByStudentIdQuery(student?.idStudent, {
      enabled: !!student?.idStudent,
    });
  const {
    control: controlSubscription,
    handleSubmit: handleSubmitCreateSubscription,
    getValues: getValuesCreateSubscription,
    setValue: setValueCreateSubscription,
    formState: { errors: errorsCreateSubscription },
    watch: watchCreateSubscription,
    trigger: triggerCreateSubscription,
    resetField: resetFieldCreateSubscription,
  } = useForm<{
    level: number;
    semester: number;
    year: number;
    subjects?: any[];
    subjectsSubscriptions?: any[];
    payment?: {
      isPaymentByEntityReference?: boolean;
      entity?: string;
      totalPrice?: string | number;
      reference?: string;
      idStudentDebt?: string;
      paymentProofSubscription?: any;
      paymentProofPerSubject?: any;
      paymentProof?: any;
      idBankEntity?: string;
      idPaymentTypeSubscription?: string;
      idPaymentTypePerSubject?: string;
      idPaymentTypeMonograph?: string;
    };
  }>({
    resolver: yupResolver(CreateSubscriptionFormValidation),
    defaultValues: {
      level: 0,
      semester: 0,
      year: 0,
      subjects: [],
      subjectsSubscriptions: [],
      payment: {
        paymentProof: null,
        isPaymentByEntityReference: true,
      },
    },
    mode: "onBlur",
  });

  const handleChangeLevelCreateSubscription = (value: string) => {
    setValueCreateSubscription("subjectsSubscriptions", []);

    setValueCreateSubscription("level", +value);

    const semesterValue = getValuesCreateSubscription("semester");
    if (+semesterValue !== 0 && +value !== 0) {
      refreshPlanSubjects();
    }

    setValueCreateSubscription("year", +value);
  };

  const handleChangeSemesterCreateSubscription = (value: string) => {
    setValueCreateSubscription("subjectsSubscriptions", []);

    setValueCreateSubscription("semester", +value);

    const levelValue = getValuesCreateSubscription("level");
    if (+levelValue !== 0 && +value !== 0) {
      refreshPlanSubjects();
    }
  };

  const {
    isLoading: isLoadingGetStudent,
    refetch: fetchStudentById,
    isError: isErrorGetStudent,
    error,
  } = useQuery(
    ["getStudentById"],
    async () => {
      try {
        const _studentData = await getStudentById(student?.idStudent);
        // if (!_studentData?.isValidated) {
        //   toast.error("Estudante não validado, contacte a secretaria");
        //   throw new Error("Estudante não validado, contacte a secretaria");
        //   return;
        // }
        if (!_studentData) return;
        const _userData = await getUserById(_studentData.idUser);
        if (!_userData) return;
        dispatch(setStudentData(_studentData));
        dispatch(setUserData(_userData));
        fetchReprovedSubjects();
      } catch (error) {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    },
    {
      enabled: !!student?.idStudent,
    }
  );

  const {
    data: reprovedSubjectsData,
    isLoading: isLoadingReprovedSubjects,
    refetch: fetchReprovedSubjects,
    isError: isErrorReprovedSubjects,
    status: reprovedSubjectsStatus,
  } = useQuery(
    ["getSubjectsReprovedQuery", `${student?.idStudent}`],
    async () => {
      try {
        const data = await getSubjectsReprovedQuery({
          PageNumber: currentPage,
          PageSize: 400,
          IdStudent: `${student?.idStudent}`,
          Year: currentYear?.year,
          Semester: getValuesCreateSubscription("semester"),
        });
        return data;
      } catch {}
    },
    {
      enabled:
        !!student?.idStudent && !!getValuesCreateSubscription("semester"),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const onSubmit = handleSubmit(async (data) => {});

  const onSubmitCreateSubscription = handleSubmitCreateSubscription(
    async (data) => {
      try {
        let referenceCode;
        let studentDebtData;
        let idSubscription;
        let dataToPrint;
        const createSubscriptionRequest: SubscriptionType = {
          ...data,
          idEnrollment: `${studentData?.idEnrollment}`,
          idAcademicYear: currentYear?.id,
          status: true,
          reason: "",
          student: userData.name,
          academicYear: currentYear?.year,
          idValidated: false,
        };
        const resultCreateSubscription =
          await mutationCreateSubscription.mutateAsync(
            createSubscriptionRequest
          );
        if (!resultCreateSubscription) return;
        dataToPrint = {
          ...createSubscriptionRequest,
          id: resultCreateSubscription,
        };
        try {
          idSubscription = resultCreateSubscription;
          const subscriptionSubjects = allAvailableSubjects.map((s) => {
            return {
              ...s,
              status: true,
              idSubscription: resultCreateSubscription,
            };
          });
          await Promise.all(
            subscriptionSubjects.map((cv) => {
              if (cv) {
                return mutationCreateSubscriptionSubject.mutateAsync(cv);
              }
            })
          );
          try {
            const uniqueSubjects = Array.from(
              new Set(subscriptionSubjects.map((subject) => subject.idSubject))
            ).map((idSubject) =>
              subscriptionSubjects.find(
                (subject) => subject.idSubject === idSubject
              )
            );

            let amountToPay = 0;
            const subscriptionAmount =
              paymentTypesData?.items.find(
                (paymentType) =>
                  paymentType.id === data?.payment?.idPaymentTypeSubscription!
              )?.amount ?? 0;
            amountToPay += subscriptionAmount;

            const subjectsAmount =
              paymentTypesData?.items.find(
                (paymentType) =>
                  paymentType.id === data?.payment?.idPaymentTypePerSubject!
              )?.amount ?? 0;
            const subjectsTotal =
              uniqueSubjects.filter(
                (ss: any) => !stringToBoolean(ss?.isMonograph)
              ).length * subjectsAmount;
            amountToPay += subjectsTotal;

            const subjectsMonographAmount =
              paymentTypesData?.items.find(
                (paymentType) =>
                  paymentType.id === data.payment?.idPaymentTypeMonograph
              )?.amount ?? 0;
            const subjectMonograph =
              (uniqueSubjects?.filter((ss: any) =>
                stringToBoolean(ss?.isMonograph)
              ).length ?? 0) * subjectsMonographAmount;
            amountToPay += subjectMonograph;

            studentDebtData = {
              idStudent: studentData?.id ?? "",
              fullname:
                userData.name
                  .concat(" ")
                  .concat(studentData?.schoolCode ?? "") ?? "",
              idPaymentType: data.payment?.idPaymentTypeSubscription!,
              originalAmount: amountToPay,
              currentAmount: amountToPay,
              dueDate: formatDateForInput(
                addDaysToDate(new Date(), 30).toISOString()
              ),
              isPayed: stringToBoolean(data.payment?.isPaymentByEntityReference)
                ? false
                : true,
              description:
                paymentTypesData?.items.find(
                  (paymentType) =>
                    paymentType.id === data?.payment?.idPaymentTypeSubscription!
                )?.description ?? "Inscrição",
              idSubscription: idSubscription,
              idEnrollment: studentData?.idEnrollment ?? "",
              idsSubjects: JSON.stringify(
                uniqueSubjects.map((s: any) => s.idSubject)
              ),
            };
            const resultCreateStudentDebt =
              await mutationCreateStudentDebt.mutateAsync(studentDebtData);
            studentDebtData = {
              ...studentDebtData,
              id: resultCreateStudentDebt,
            };
            if (
              stringToBoolean(
                getValuesCreateSubscription(
                  "payment.isPaymentByEntityReference"
                )
              )
            ) {
              const currentDate = new Date();
              try {
                referenceCode = await getReferenceCodeV2({
                  amount: studentDebtData.currentAmount,
                  idStudent: studentDebtData.idStudent,
                  idPaymentType: studentDebtData.idPaymentType,
                  idStudentDebt: studentDebtData.id,
                  month: (currentDate.getMonth() + 1).toString(),
                  year: currentDate.getFullYear().toString(),
                  description: studentDebtData.description,
                  idBankEntity: data?.payment?.idBankEntity,
                  entity: String(
                    schoolBankConfigsData?.items.find(
                      (i) =>
                        i.id ===
                        getValuesCreateSubscription("payment.idBankEntity")
                    )?.bankEntity
                  ),
                });
                const paymentReference: PaymentReferenceType = {
                  idStudent: studentDebtData.idStudent,
                  idPaymentType: studentDebtData.idPaymentType,
                  amount: studentDebtData.currentAmount,
                  dueDate: new Date(studentDebtData.dueDate).toISOString(),
                  referenceCode: referenceCode,
                  idStudentDebt: studentDebtData.id,
                  entity: String(
                    schoolBankConfigsData?.items.find(
                      (i) =>
                        i.id ===
                        getValuesCreateSubscription("payment.idBankEntity")
                    )?.bankEntity
                  ),
                  idBankEntity: data?.payment?.idBankEntity,
                };
                await mutationSaveReferenceCode.mutateAsync(paymentReference);
              } catch (ex: any) {
                toast.error(ex?.message);
              }
            } else {
              const { base64 } = await getFile(data.payment?.paymentProof);
              const paymentTransaction: PaymentTransactionType = {
                date: new Date().toISOString(),
                idPaymentType: data.payment?.idPaymentTypeSubscription!,
                amountPaid: amountToPay ?? 0,
                idStudent: studentDebtData.idStudent,
                paymentProof: base64,
                idStudentDebt: resultCreateStudentDebt,
                idCompleted: true,
              };
              await mutationCreatePaymentTransaction.mutateAsync(
                paymentTransaction
              );
            }
            dataToPrint.course = studentData?.course;
            dataToPrint.payment = {
              reference: referenceCode,
              paymentProof: data?.payment?.paymentProof ?? "",
              entity: String(
                schoolBankConfigsData?.items.find(
                  (i) =>
                    i.id === getValuesCreateSubscription("payment.idBankEntity")
                )?.bankEntity
              ),
              totalPrice: studentDebtData?.currentAmount,
              bank: String(
                schoolBankConfigsData?.items.find(
                  (i) =>
                    i.id === getValuesCreateSubscription("payment.idBankEntity")
                )?.bankName
              ),
            };
          } catch (error) {
            console.log({ error });
          }
        } catch (error) {
          console.log({ error });
        }

        const sdStudent = await getStudentById(studentData?.id);
        const coursePayments = await getCoursePayments({
          PageNumber: 1,
          PageSize: 1,
          IdCourse: sdStudent?.idCourse,
          Regime: sdStudent?.regime,
          IsToDebtsGenerateAutomatically: true,
        });
        if (
          coursePayments &&
          coursePayments.items &&
          coursePayments.items.length > 0
        ) {
          const monthlyPayment = coursePayments.items
            .filter((cp) => cp.isToDebtsGenerateAutomatically)
            ?.at(0);

          if (monthlyPayment) {
            let paymentType = paymentTypesData?.items.find(
              (pt) => pt.id === monthlyPayment.idPaymentType
            );
            if (
              paymentTypesData &&
              allAvailableSubjects?.length === 1 &&
              monthlyPayment.isOneSubjectSubscribed &&
              monthlyPayment.idPaymentWhenOneSubjectSubscribed
            ) {
              // if subject is monograph, get monograph payment type from monthly payment
              paymentType =
                paymentTypesData.items
                  .filter(
                    (pt) =>
                      pt.id == monthlyPayment.idPaymentWhenOneSubjectSubscribed!
                  )
                  ?.at(0) ?? paymentType;

              if (allAvailableSubjects?.[0]?.isMonograph) {
                paymentType =
                  paymentTypesData.items
                    .filter(
                      (pt) =>
                        pt.id ==
                        monthlyPayment.idPaymentWhenOneSubjectSubscribedMonography!
                    )
                    ?.at(0) ?? paymentType;
              }
            }
            if (paymentType) {
              const createSubscriptionDebtReference: CreateStudentDebtReferenceType =
                {
                  studentId: studentData?.id ?? "",
                  studentCode: studentData?.schoolCode ?? "",
                  fullname: studentData?.fullname ?? "",
                  enrollmentId: studentData?.idEnrollment ?? "",
                  subscriptionId: idSubscription ?? "",
                  paymentTypeId: `${paymentType.id}`,
                  idSchoolBankConfig: paymentType.preferedBank
                    ? `${paymentType.preferedBank}`
                    : String(
                        schoolBankConfigsData?.items.find(
                          (i) =>
                            i.id ===
                            getValuesCreateSubscription("payment.idBankEntity")
                        )?.id
                      ),
                  bankEntity: paymentType?.preferedBank
                    ? `${
                        schoolBankConfigsData?.items.find(
                          (i) => i.id === paymentType?.preferedBank
                        )?.bankEntity ?? ""
                      }`
                    : String(
                        schoolBankConfigsData?.items.find(
                          (i) =>
                            i.id ===
                            getValuesCreateSubscription("payment.idBankEntity")
                        )?.bankEntity
                      ),
                  semester: data.semester ?? 1,
                  year:
                    Number(currentYear?.year) ??
                    Number(new Date().getFullYear()),
                };
              await handleCreateReferencesForMonthlyDebts(
                createSubscriptionDebtReference
              );
            }
          }
        }
        const paymentPrintData: PaymentPrintData = {
          isPaymentByEntityReference: stringToBoolean(
            getValuesCreateSubscription("payment.isPaymentByEntityReference")
          ),
          reference: referenceCode,
          entity: String(
            schoolBankConfigsData?.items.find(
              (i) =>
                i.id === getValuesCreateSubscription("payment.idBankEntity")
            )?.bankEntity
          ),
          bank: String(
            schoolBankConfigsData?.items.find(
              (i) =>
                i.id === getValuesCreateSubscription("payment.idBankEntity")
            )?.bankName
          ),
          totalPrice: studentDebtData?.currentAmount,
        };

        //
        toast.success("Estudante inscrito");
        handleBack();
        handlePrintSubscription(dataToPrint, allAvailableSubjects, paymentPrintData);
      } catch (err: any) {  
        toast.error(`${err?.message}`);
      }
    }
  );

  const handleCreateReferencesForMonthlyDebts = async (
    createSubscriptionDebtReference: CreateStudentDebtReferenceType
  ) => {
    try {
      const references = await createStudentDebtReferences({
        ...createSubscriptionDebtReference,
      });
      toast.success(
        "Referências mensais criadas, verifique na secção de finanças."
      );
      // const pdfData = { references: references, data: createSubscriptionDebtReference };
      //          localStorage.setItem(
      //            "studentDebtReferencesData",
      //            JSON.stringify(pdfData)
      //          );
      //           const pdfPreviewTypeDataLS = "REFERENCES";
      //      localStorage.setItem("pdfPreviewTypeData_REFS", pdfPreviewTypeDataLS);
      //          dispatch(setReferencesData(pdfData));
      //          window.open(routes.pdfPreview, "_blank");
    } catch (error) {
      console.error({ error });
    }
  };
  const navigateAndValidate = (tabToNavigateTo: ActiveSegmentTab) => {
    if (activeSegment !== tabToNavigateTo) setActiveSegment(tabToNavigateTo);
    triggerCreateSubscription();
  };

  const handleBack = () => {
    dispatch(resetStudentData());
    dispatch(resetUserData());
    navigate(routes.studentSubscription);
  };

  const {
    data: planSubjectsData,
    isLoading: isLoadingPlanSubjectsData,
    refetch: refreshPlanSubjects,
    status: planSubjectsStatus,
    isError: isErrorPlanSubjectsData,
  } = useQuery(
    ["getPlanSubjectsQuery"],
    async () => {
      try {
        const data = await getPlanSubjects({
          PageSize: 400,
          PageNumber: 1,
          Level: undefined,
          Semester: getValuesCreateSubscription("semester"),
          IdPlan: studentData?.idPlan,
          IdCourse: studentData?.idCourse,
        });
        const responseData = { ...data };
        responseData.items = responseData.items.filter(
          (i) => i.level <= getValuesCreateSubscription("year")!
        );
        return responseData;
      } catch {}
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const {
    data: courses,
    isLoading: isLoadingCourses,
    isError: errorCourses,
    refetch: refreshCourses,
  } = useGetCoursesQuery(
    {
      PageNumber: 1,
      PageSize: 400,
      IdDepartament: studentData?.idDepartament,
      Id: studentData?.idCourse,
    },
    { enabled: !!studentData?.idDepartament }
  );

  const course = courses?.items.find((i) => i.id === studentData?.idCourse);
  const watchLevelOrYear = watchCreateSubscription("year");
  const isLastYearOrLevel =
    Number(course?.duration) === Number(watchLevelOrYear);

  const { data: processModelsData, status: processModelsStatus } =
    useGetProcessModelsQuery(
      {
        PageNumber: 1,
        PageSize: 2,
        ProcessName: processModelName_preSubscription,
        CourseLevel: course?.level,
        CourseModality: course?.modality,
      },
      { enabled: !!course && !isEmpty(processModelName_preSubscription) }
    );

  const handlePrintSubscription = async (
    s: SubscriptionType,
    subjects: any[],
    payment?: PaymentPrintData
  ) => {
    try {
      dispatch(setLoaderAvailable(true));
      const data: any = {
        type: "SUBSCRIPTION",
        outputFormat: "DOCX",
        data: {
          ies: IES.NAME ?? "",
          ano_lectivo: s.academicYear ?? "",
          nome_completo: s.student ?? "",
          codigo: studentData?.schoolCode ?? "",
          curso: s.course,
          data_emissao: new Date().toLocaleDateString(),
          utilizador: loginData?.Fullname ?? "",
          cargo: loginData?.Role ?? "",
          grades: subjects.map((sub, index) => ({
            ordem: index + 1,
            nome_disciplina: sub.subject,
            nivel: s.level,
            semestre: s.semester,
          })),
        },
        logoBase64: logoBase64 ?? "",
      };
      if (payment?.isPaymentByEntityReference) {
        data.type = "SUBSCRIPTION_REFERENCE";
        data.data.valor = payment.totalPrice ?? 0;
        data.data.referencia = payment.reference ?? "";
        data.data.banco = payment.bank ?? "";
        data.data.entidade = payment.entity ?? "";
      }

      const blob = await exportDocument(data, "SUBSCRIPTION");
      toast.success("Documento de inscrição exportado com sucesso!");
    } catch (err) {
      console.error("Erro ao imprimir inscrição:", err);
      toast.error("Erro ao exportar o documento de inscrição.");
    } finally {
      dispatch(resetLoaderAvailable());
    }
  };
  const watchSubjectSubscriptions = watchCreateSubscription(
    "subjectsSubscriptions"
  );
  const watchSubjects = watchCreateSubscription("subjects");
  const subjectSubscriptionsSelected = watchSubjectSubscriptions?.map((ss) => {
    ss = JSON.parse(ss);
    return {
      idSubject: ss.idSubject,
      level: ss.level,
      semester: ss.semester,
      isCore: ss.isCore,
      idSubjectPrecedence: ss.idSubjectPrecedence,
      subject: ss.subject,
      isMonograph: ss.isMonograph,
    };
  });
  const repprovedSubjectsSelected = watchSubjects?.map((ss) => {
    ss = JSON.parse(ss);
    return {
      idSubject: ss.idSubject,
      level: ss.level,
      semester: ss.semester,
      isCore: ss.isCore,
      idSubjectPrecedence: ss.idSubjectPrecedence,
      subject: ss.subject,
      isMonograph: ss.isMonograph,
    };
  });
  const allAvailableSubjects = [
    ...(repprovedSubjectsSelected ?? []),
    ...(subjectSubscriptionsSelected ?? []),
  ];

  if (isLoadingStudent || isLoadingGetStudent) {
    return <Spinner size="3" />;
  }

  if (isErrorGetStudent) {
    return <Text></Text>;
  }

  if (!student || !student.idStudent) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Entre como estudante &nbsp;
            <Link to={routes.dashboard} style={{ color: "var(--gray-12)" }}>
              Voltar
            </Link>
          </Text>
        </Callout.Root>
      </Flex>
    );
  }

  if (studentDebtsTotalsData && studentDebtsTotalsData.totalDebt > 0) {
    return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Contacte a secretaria para regularizar a sua situação financeira
            &nbsp;
          </Text>
        </Callout.Root>
      </Flex>
    );
  }

if(studentData?.idAcademicYear !== currentYear?.id){
     return (
      <Flex direction="column">
        <Callout.Root color="red" role="alert">
          <Text>
            Por favor, Renove a sua matricula para o ano corrente
            &nbsp;
          </Text>
        </Callout.Root>
      </Flex>
    );
} 
return (
    <Card size="3">
      <Flex direction="column" gap="6">
        <form onSubmit={onSubmitCreateSubscription} noValidate>
          <Tabs.Root
            value={activeSegment}
            onValueChange={(tabToNavigate) => {
              navigateAndValidate(tabToNavigate as ActiveSegmentTab);
              if (tabToNavigate.includes("subjects")) {
                fetchReprovedSubjects();
                refreshPlanSubjects();
              }
              if (tabToNavigate.includes("payments")) {
                setValueCreateSubscription(
                  "payment.idBankEntity",
                  processModelsData?.items.at(0)?.idSchoolBankConfig
                );
                setValueCreateSubscription(
                  "payment.idPaymentTypeSubscription",
                  paymentTypesData?.items
                    ?.filter(
                      (i) =>
                        Number(i?.internalCode!) === 26 &&
                        processModelsData?.items
                          .at(0)
                          ?.idsPaymentTypes?.includes(i.id ?? "")
                    )
                    .at(0)?.id
                );
                setValueCreateSubscription(
                  "payment.idPaymentTypePerSubject",
                  paymentTypesData?.items
                    ?.filter(
                      (i) =>
                        Number(i?.internalCode!) === 27 &&
                        processModelsData?.items
                          .at(0)
                          ?.idsPaymentTypes?.includes(i.id ?? "")
                    )
                    .at(0)?.id
                );

                if (allAvailableSubjects.find((s) => s.isMonograph)) {
                  setValueCreateSubscription(
                    "payment.idPaymentTypeMonograph",
                    paymentTypesData?.items
                      ?.filter(
                        (i) =>
                          Number(i?.internalCode!) === 28 &&
                          processModelsData?.items
                            .at(0)
                            ?.idsPaymentTypes?.includes(i.id ?? "")
                      )
                      .at(0)?.id
                  );
                } else {
                  setValueCreateSubscription(
                    "payment.idPaymentTypeMonograph",
                    ""
                  );
                }
              }
            }}
          >
            <Tabs.List>
              <Tabs.Trigger value="user">Dados gerais</Tabs.Trigger>
              <Tabs.Trigger value="departament">Curso</Tabs.Trigger>
              <Tabs.Trigger value="subscription">
                Dados de inscrição
                {errorsCreateSubscription && (
                  <Badge color="red">
                    {Object.keys(errorsCreateSubscription)?.length}{" "}
                  </Badge>
                )}
              </Tabs.Trigger>
              <Tabs.Trigger value="subjects">Disciplinas</Tabs.Trigger>
              <Tabs.Trigger value="payments">Pagamentos</Tabs.Trigger>
              <Tabs.Trigger value="validate">
                Validação da inscrição
              </Tabs.Trigger>
            </Tabs.List>
            <Box pt="3" style={{ overflow: "auto" }}>
              <Tabs.Content value="user">
                <UserPreview />
              </Tabs.Content>
              <Tabs.Content value="departament">
                <Flex direction="column" gap="4">
                  <Text weight="bold">Departamento:</Text>
                  {studentData?.departament ? (
                    <Text>{studentData?.departament}</Text>
                  ) : (
                    <Text color="gray">Nenhum departamento encontrado</Text>
                  )}

                  <Text weight="bold">Curso:</Text>
                  {studentData?.course ? (
                    <Text>{studentData?.course}</Text>
                  ) : (
                    <Text color="gray">Nenhum curso encontrado</Text>
                  )}
                </Flex>
              </Tabs.Content>
              <Tabs.Content value="subjects">
                <CheckboxGroupComponent
                  name="subjects"
                  label="Disciplinas em atraso"
                  control={controlSubscription}
                  errors={errorsCreateSubscription}
                  options={
                    reprovedSubjectsData?.items &&
                    reprovedSubjectsData?.items
                      ?.sort((a, b) => a.level - b.level)
                      ?.map((reprovedSubjects) => {
                        return {
                          value: JSON.stringify(reprovedSubjects),
                          label: `${reprovedSubjects.subject} - ${reprovedSubjects.semester}º Semestre`,
                        };
                      })
                  }
                  style={{ width: "100%" }}
                  itemsError={isErrorReprovedSubjects}
                  itemsLoading={isLoadingReprovedSubjects}
                />

                <CheckboxGroupComponent
                  name="subjectsSubscriptions"
                  label="Disciplinas do semestre"
                  control={controlSubscription}
                  errors={errorsCreateSubscription}
                  options={planSubjectsData?.items
                    ?.sort((a, b) => a.level - b.level)
                    ?.map((item) => {
                      const isAbleToDoEqual = reprovedSubjectsData?.items.find(
                        (rs) => rs.idSubject === item.idSubjectPrecedence
                      );
                      return {
                        value: JSON.stringify({
                          id: item.id,
                          subject: item.subject,
                          semester: item.semester,
                          idSubject: item.idSubject,
                          level: item.level,
                          idSubjectPrecedence: item.idSubjectPrecedence,
                          isCore: item.isCore,
                          isMonograph: item.isMonograph,
                        }),
                        label:
                          `${item.subject} (${item.level}ºAno ${item.semester}ºSemestre)`.concat(
                            (item?.isCore
                              ? isAbleToDoEqual &&
                                isAbleToDoEqual?.id &&
                                " (Nuclear)  Não será inscrito"
                              : isAbleToDoEqual &&
                                isAbleToDoEqual?.id &&
                                " (Opcional)  Não será inscrito") ?? ""
                          ),
                        isCoreBadge: item.isCore ? (
                          <>
                            <Badge color="green">Nuclear</Badge>
                            {isAbleToDoEqual && isAbleToDoEqual?.id && (
                              <Badge color="red">Não será inscrito</Badge>
                            )}
                          </>
                        ) : (
                          <>
                            <Badge color="gray">Opcional</Badge>
                            {isAbleToDoEqual && isAbleToDoEqual?.id && (
                              <Badge color="red">Não será inscrito</Badge>
                            )}
                          </>
                        ),
                      };
                    })}
                  style={{ width: "100%" }}
                  itemsError={isErrorPlanSubjectsData}
                  itemsLoading={isLoadingPlanSubjectsData}
                />
              </Tabs.Content>
              <Tabs.Content value="subscription">
                <Flex direction="row" gap="8">
                  <TextFieldComponent
                    name="level"
                    label="Nível:"
                    control={controlSubscription}
                    errors={errorsCreateSubscription}
                    style={{ width: "100%" }}
                    fieldProps={{
                      placeholder:
                        "Digite o nível a que se inscreve o estudante",
                      onChange: (e: any) =>
                        handleChangeLevelCreateSubscription(e.target.value),
                    }}
                    required={true}
                  />
                  <TextFieldComponent
                    name="semester"
                    label="Semestre:"
                    control={controlSubscription}
                    errors={errorsCreateSubscription}
                    style={{ width: "100%" }}
                    fieldProps={{
                      placeholder: "Digite o semestre",
                      onChange: (e: any) =>
                        handleChangeSemesterCreateSubscription(e.target.value),
                    }}
                    required={true}
                  />
                </Flex>
              </Tabs.Content>
              <Tabs.Content value="payments">
                <Flex gap="6" direction="column">
                  <SelectComponent
                    name="payment.idBankEntity"
                    label="Entidade Bancária"
                    control={controlSubscription}
                    errors={errorsCreateSubscription}
                    itemsLoading={isLoadingSchoolschoolBankConfigs}
                    itemsError={isErrorSchoolschoolBankConfigs}
                    options={
                      schoolBankConfigsData?.items
                        ?.filter(
                          (item) =>
                            item.id ==
                            processModelsData?.items.at(0)?.idSchoolBankConfig
                        )
                        .map((item) => ({
                          value: item.id!,
                          label: String(item.bankEntity!) ?? "",
                        })) ?? []
                    }
                    style={{ width: "100%" }}
                    fieldProps={{ disabled: true }}
                  />
                  <SelectComponent
                    name="payment.idPaymentTypeSubscription"
                    label="Valor da inscrição"
                    control={controlSubscription}
                    errors={errorsCreateSubscription}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={
                      paymentTypesData?.items
                        ?.filter(
                          (i) =>
                            Number(i?.internalCode!) === 26 &&
                            processModelsData?.items
                              .at(0)
                              ?.idsPaymentTypes?.includes(i.id ?? "")
                        )
                        .map((item) => ({
                          value: item.id!,
                          label: String(item.description!) ?? "",
                        })) ?? []
                    }
                    style={{ width: "100%" }}
                    fieldProps={{ disabled: true }}
                  />
                  <SelectComponent
                    name="payment.idPaymentTypePerSubject"
                    label="Valor por disciplina"
                    control={controlSubscription}
                    errors={errorsCreateSubscription}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={
                      paymentTypesData?.items
                        ?.filter(
                          (i) =>
                            Number(i?.internalCode!) === 27 &&
                            processModelsData?.items
                              .at(0)
                              ?.idsPaymentTypes?.includes(i.id ?? "")
                        )
                        .map((item) => ({
                          value: item.id!,
                          label: String(item.description!) ?? "",
                        })) ?? []
                    }
                    style={{ width: "100%" }}
                    fieldProps={{ disabled: true }}
                  />

                  <SelectComponent
                    name="payment.idPaymentTypeMonograph"
                    label="Valor da monografia"
                    control={controlSubscription}
                    errors={errorsCreateSubscription}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={
                      paymentTypesData?.items
                        ?.filter((i) => Number(i?.internalCode!) === 28)
                        .map((item) => ({
                          value: item.id!,
                          label: String(item.description!) ?? "",
                        })) ?? []
                    }
                    style={{ width: "100%" }}
                    fieldProps={{ disabled: true }}
                  />
                </Flex>
              </Tabs.Content>
              <Tabs.Content value="validate">
                <Box style={{ overflow: "auto" }}>
                  <Flex direction="column" gap="4">
                    <Flex gap="7">
                      <Flex gap="3">
                        <Text weight="bold">Estudante:</Text>
                        <Text>{userData?.name || "Nome não encontrado"}</Text>
                      </Flex>
                      <Flex gap="3">
                        <Text weight="bold">Curso:</Text>
                        <Text>
                          {studentData?.course || "Curso não encontrado"}
                        </Text>
                      </Flex>
                      <Flex gap="3">
                        <Text weight="bold">Ano:</Text>
                        <Text>
                          {watchCreateSubscription("level") ||
                            "Ano não especificado"}
                        </Text>
                      </Flex>
                    </Flex>
                    <Flex gap="7">
                      <Flex gap="3">
                        <Text weight="bold">Semestre:</Text>
                        <Text>
                          {watchCreateSubscription("semester") ||
                            "Semestre não especificado"}
                        </Text>
                      </Flex>
                    </Flex>
                    <Text weight="bold" mt="4">
                      Resumo:
                    </Text>
                    <Flex direction="column" gap="4" style={{ flex: 1 }}>
                      <Lists
                        status={"success"}
                        TableComponent={() => (
                          <TableComponent
                            columns={Object.keys(columnNamesMapPlanSubjects)}
                            items={allAvailableSubjects.map((item) => {
                              const isAbleToDoEqual =
                                reprovedSubjectsData?.items.find(
                                  (rs) =>
                                    rs.idSubject === item.idSubjectPrecedence
                                );
                              return {
                                ...item,
                                isCoreBagde: item.isCore ? (
                                  <>
                                    {" "}
                                    <Badge color="green">Nuclear </Badge>
                                    {isAbleToDoEqual && isAbleToDoEqual?.id ? (
                                      <>
                                        <Badge color="red">
                                          Não será inscrito
                                        </Badge>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Badge color="gray">Opcional</Badge>{" "}
                                    {isAbleToDoEqual && isAbleToDoEqual?.id ? (
                                      <>
                                        <Badge color="red">
                                          Não será inscrito
                                        </Badge>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </>
                                ),
                              };
                            })}
                            options={[]}
                            columnNamesMap={columnNamesMapPlanSubjects}
                            totalPages={1}
                            currentPage={1}
                            onPageChange={() => {}}
                          />
                        )}
                      />
                    </Flex>
                  </Flex>

                  <Flex mt="4" justify="end">
                    <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                      <Button
                        color="red"
                        onClick={handleBack}
                        disabled={mutationCreateSubscription.isLoading}
                      >
                        <ChevronLeftIcon /> voltar
                      </Button>
                      <Button
                        type="submit"
                        disabled={mutationCreateSubscription.isLoading}
                        variant="surface"
                      >
                        {mutationCreateSubscription.isLoading ? (
                          <Spinner loading>
                            {" "}
                            <EnterIcon />
                          </Spinner>
                        ) : (
                          <EnterIcon />
                        )}
                        Registar
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              </Tabs.Content>
            </Box>
          </Tabs.Root>
        </form>
      </Flex>
    </Card>
  );
};
